/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, atom, PrimitiveAtom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { activeWorkspaceAtom } from 'jotai/workspace';
import { isEqual } from 'lodash';
import {
  AlertData,
  AlertRequest,
  AlertType,
  AudioAlertRequest,
  FlagAlertRequest,
  LapActivityRequest,
  LapSearchRequest,
  PitAlertRequest,
  RunType,
  TextSearchRequest,
} from 'models/viewModels';
import { useMemo } from 'react';
import { alertService } from 'services/alert/AlertService';
import { AlertNotificationLogSettingsObject } from './AlertNotificationLogSettingsObject';
import { getAlertTypeFromFormattedName } from './AlertNotificationObject';

// Alert Data by settingsId, is set by streamManagement
export const alertNotificationAtomFamily = atomFamily<string, PrimitiveAtom<AlertData[] | undefined>>(
  (params: string) => atom<AlertData[] | undefined>(undefined),
  (a, b) => isEqual(a, b)
);

// All Alert Data by Filter, is called by streamManagement
export const alertNotificationReplayAtomFamily = atomFamily<AlertRequest, Atom<Promise<AlertData[]>>>(
  (params: AlertRequest) =>
    atom<Promise<AlertData[]>>(async () => {
      const replay = (await alertService.getAlertReplay(params)).alertDataSet;
      return replay;
    }),
  (a, b) => isEqual(a, b)
);

// SettingsId that is currently being used for the alert notification toasts
export const overwrittenAlertToastSettingsIdAtom = atom<string | undefined>(undefined);
export const alertToastSettingsIdAtom = atom<string | undefined, [string | undefined], void>(
  (get) => {
    if (get(overwrittenAlertToastSettingsIdAtom) !== undefined) {
      return get(overwrittenAlertToastSettingsIdAtom);
    }
    // get the workspace and find the settingsId with enableNotifications set to true
    const currentWorkspace = get(activeWorkspaceAtom);
    // check if workspace is not empty / not ready
    if (
      Object.keys(currentWorkspace).length > 0 &&
      Object.keys(currentWorkspace).includes('workspace') &&
      currentWorkspace?.workspace?.visualizationSettings !== undefined &&
      Object.keys(currentWorkspace?.workspace?.visualizationSettings?.byId).length > 0
    ) {
      // find the settingsId with enableNotifications set to true
      const visualizationSettings = currentWorkspace?.workspace?.visualizationSettings;
      // byId is Record<string, T> where T is the type of the settings
      return Object.entries(visualizationSettings.byId).find(([_, settings]) => {
        return settings.enableNotifications === true;
      })?.[0]; // return the settingsId (the key)
    }
    return undefined;
  },
  (_get, set, newValue: string | undefined) => {
    set(overwrittenAlertToastSettingsIdAtom, newValue);
  }
);

export const getVehicleNumbersByAlertType = (
  settings: AlertNotificationLogSettingsObject,
  highlightedGlobalCompGroupVehNums: string[],
  alertType: AlertType
) => {
  // backend assumes undefined or 0 length means all vehicles
  return settings.alertVehicleSelections[alertType].allVehicles
    ? []
    : settings.alertVehicleSelections[alertType].localVehicleSelections.useLocalSelections
    ? settings.alertVehicleSelections[alertType].localVehicleSelections.individualSelections
    : highlightedGlobalCompGroupVehNums;
};

export const useAlertFilter = (
  settings: AlertNotificationLogSettingsObject,
  highlightedCompGroupVehs: string[],
  audioKeywords: string[],
  lapSearchRequest: LapSearchRequest | undefined,
  titleSearchRequest: TextSearchRequest[] | undefined,
  descriptionSearchRequest: TextSearchRequest[] | undefined,
  seriesType: number,
  runType: RunType,
  sessionId: string,
  trackEventId: string | undefined
) => {
  return useMemo(() => {
    const includedCategories = Object.entries(settings.categories)
      .filter(([_, value]) => value)
      .map(([key, _]) => getAlertTypeFromFormattedName(key));
    const pitRequest: PitAlertRequest | undefined = includedCategories.includes(AlertType.PIT)
      ? {
          vehicleNumbers: getVehicleNumbersByAlertType(settings, highlightedCompGroupVehs, AlertType.PIT),
        }
      : undefined;
    const flagRequest: FlagAlertRequest | undefined = includedCategories.includes(AlertType.FLAG) ? {} : undefined;
    const audioRequest: AudioAlertRequest | undefined = includedCategories.includes(AlertType.AUDIO)
      ? {
          vehicleNumbers: getVehicleNumbersByAlertType(settings, highlightedCompGroupVehs, AlertType.AUDIO),
          keywords: audioKeywords,
        }
      : undefined;
    // TODO: Set to undefined when more overarching lap activity category toggled is implemented
    const lapActivityRequest: LapActivityRequest | undefined = {
      luckyDogRequest: {
        exclude: !includedCategories.includes(AlertType.LUCKYDOG),
      },
    };
    return {
      flagRequest: flagRequest,
      pitRequest: pitRequest,
      audioRequest: audioRequest,
      lapSearchRequest: lapSearchRequest,
      lapActivityRequest: lapActivityRequest,
      titleSearch: titleSearchRequest,
      descriptionSearch: descriptionSearchRequest,
      seriesType: seriesType,
      runType: runType,
      sessionId: sessionId,
      eventId: trackEventId || '',
    };
  }, [
    settings,
    highlightedCompGroupVehs,
    seriesType,
    runType,
    sessionId,
    lapSearchRequest,
    titleSearchRequest,
    descriptionSearchRequest,
    trackEventId,
    audioKeywords,
  ]);
};
